import React, { useEffect } from "react";
import {
  BarChart,
  XAxis,
  YAxis,
  Bar,
  Cell,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import Loader from "../../components/Common/Loader";
import appInsights from "../../AppInsights";

function AvgLife({ List, loadingdata }) {
  const today = new Date();
  
  // Track component load and data processing
  useEffect(() => {
    const openTime = new Date().toLocaleString();
    appInsights.trackEvent({
      name: "AvgLife_Component_Loaded",
      properties: {
        openedAt: openTime,
        initialListLength: List?.length || 0,
        loadingState: loadingdata,
        component: "Client_Per_Team_Member_Chart",
      },
    });
  }, []);

  const clientLifetimes = List.map((client) => {
    const startDate = new Date(client.startDate);
    const endDate = client.endDate ? new Date(client.endDate) : today;
    const lifetime = (endDate - startDate) / (1000 * 60 * 60 * 24);
    return { ...client, lifetime };
  }).filter((client) => client.lifetime > 30);

  // Track data processing results
  useEffect(() => {
    if (List?.length > 0 && !loadingdata) {
      appInsights.trackEvent({
        name: "AvgLife_Data_Processed",
        properties: {
          originalListLength: List.length,
          filteredListLength: clientLifetimes.length,
          clientsFilteredOut: List.length - clientLifetimes.length,
          averageLifetime: averageLifetime,
          totalClientsAnalyzed: clientLifetimes.length,
          hasValidData: clientLifetimes.length > 0,
        },
      });
    }
  }, [List, loadingdata, clientLifetimes.length]);

  const totalLifetime = clientLifetimes.reduce(
    (sum, client) => sum + client.lifetime,
    0
  );
  const averageLifetime = clientLifetimes.length
    ? (totalLifetime / clientLifetimes.length).toFixed(1)
    : 0;

  // Track chart rendering
  useEffect(() => {
    if (List?.length > 0 && !loadingdata) {
      appInsights.trackEvent({
        name: "AvgLife_Chart_Rendered",
        properties: {
          dataPoints: List.length,
          averageLifetime: averageLifetime,
          chartType: "vertical_bar",
          hasData: List.some(item => item.value > 0),
          maxValue: Math.max(...List.map(item => item.value || 0)),
          minValue: Math.min(...List.map(item => item.value || 0)),
        },
      });
    }
  }, [List, loadingdata, averageLifetime]);

  // Track loading state
  useEffect(() => {
    if (loadingdata) {
      appInsights.trackEvent({
        name: "AvgLife_Loading_State",
        properties: {
          component: "Client_Per_Team_Member_Chart",
          expectedDataPoints: List?.length || 0,
        },
      });
    }
  }, [loadingdata, List]);

  // Track empty or invalid data scenarios
  useEffect(() => {
    if (!loadingdata) {
      if (!List || List.length === 0) {
        appInsights.trackEvent({
          name: "AvgLife_No_Data",
          properties: {
            reason: "empty_list",
            component: "Client_Per_Team_Member_Chart",
          },
        });
      } else if (clientLifetimes.length === 0) {
        appInsights.trackEvent({
          name: "AvgLife_All_Clients_Filtered_Out",
          properties: {
            originalCount: List.length,
            filterCriteria: "lifetime_greater_than_30_days",
            reason: "all_clients_under_30_days",
          },
        });
      }
    }
  }, [List, loadingdata, clientLifetimes.length]);

  // FIXED: Track chart interactions
  const handleChartInteraction = (data, index, event) => {
    try {
      // Safely handle different parameter scenarios from Recharts
      let interactionData, interactionType, payload;
      
      if (typeof data === 'object' && data !== null) {
        // First parameter is the data object from Recharts
        interactionData = data;
        interactionType = event?.type || 'hover'; // Default to hover if event is undefined
      } else {
        // Parameters might be in different order, use safe defaults
        interactionData = {};
        interactionType = 'unknown';
      }

      // Extract the payload safely
      payload = interactionData.activePayload?.[0]?.payload || 
                interactionData.payload || 
                List?.find(item => item.name === interactionData.activeLabel);

      if (payload) {
        appInsights.trackEvent({
          name: "AvgLife_Chart_Interaction",
          properties: {
            dataPoint: payload.name,
            value: payload.value,
            interactionType: interactionType,
            chartType: "vertical_bar",
            clientLifetime: payload.lifetime,
            isActiveClient: !payload.endDate,
          },
        });
      } else if (interactionData.activeLabel) {
        // Track when we have a label but no payload
        appInsights.trackEvent({
          name: "AvgLife_Chart_Interaction_Label_Only",
          properties: {
            dataPoint: interactionData.activeLabel,
            interactionType: interactionType,
            chartType: "vertical_bar",
          },
        });
      }
    } catch (error) {
      console.error('Error in handleChartInteraction:', error);
      appInsights.trackException({
        exception: error,
        severityLevel: "Error",
        properties: {
          name: "AvgLife_Interaction_Error",
          component: "handleChartInteraction",
          errorMessage: error.message,
        },
      });
    }
  };

  // Alternative: Separate handlers for different interaction types
  const handleBarClick = (data, index, event) => {
    try {
      const payload = data?.payload || data?.activePayload?.[0]?.payload;
      if (payload) {
        appInsights.trackEvent({
          name: "AvgLife_Bar_Clicked",
          properties: {
            dataPoint: payload.name,
            value: payload.value,
            clientLifetime: payload.lifetime,
            isActiveClient: !payload.endDate,
          },
        });
      }
    } catch (error) {
      console.error('Error in handleBarClick:', error);
    }
  };

  const handleBarHover = (data, index, event) => {
    try {
      const payload = data?.payload || data?.activePayload?.[0]?.payload;
      if (payload) {
        appInsights.trackEvent({
          name: "AvgLife_Bar_Hovered",
          properties: {
            dataPoint: payload.name,
            value: payload.value,
            clientLifetime: payload.lifetime,
            isActiveClient: !payload.endDate,
          },
        });
      }
    } catch (error) {
      console.error('Error in handleBarHover:', error);
    }
  };

  // Track calculation details for debugging
  useEffect(() => {
    if (clientLifetimes.length > 0 && !loadingdata) {
      const lifetimeStats = {
        minLifetime: Math.min(...clientLifetimes.map(c => c.lifetime)),
        maxLifetime: Math.max(...clientLifetimes.map(c => c.lifetime)),
        medianLifetime: clientLifetimes.sort((a, b) => a.lifetime - b.lifetime)[
          Math.floor(clientLifetimes.length / 2)
        ]?.lifetime,
      };

      appInsights.trackEvent({
        name: "AvgLife_Calculation_Details",
        properties: {
          totalLifetimeDays: totalLifetime,
          clientCount: clientLifetimes.length,
          averageLifetimeDays: averageLifetime,
          ...lifetimeStats,
        },
      });
    }
  }, [clientLifetimes, totalLifetime, averageLifetime, loadingdata]);

  if (loadingdata) {
    return <Loader />;
  }

  return (
    <div className=" ">
      <div className="total-clients-container-avg ">
        <div className="website-hits-header">
          {/* Title Section */}
          <div className="website-hits-title">
            <h1>Client per team member</h1>
          </div>

          {/* Total Hits Section */}
          <div className="website-hits-total">
            <h1>Average lifetime</h1>
            <p className="total-hits-count">{averageLifetime} month</p>
          </div>
        </div>

        <div className="chart-container-avg">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={List} 
              layout="vertical"
              onClick={handleBarClick} // Use separate click handler
            >
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" />
              <Bar 
                dataKey="value" 
                fill="#A42066" 
                radius={[0, 12, 12, 0]}
                onMouseEnter={handleBarHover} // Use separate hover handler
              >
                {List.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill="#A42066" />
                ))}
                <LabelList
                  dataKey="value"
                  position="insideRight"
                  fill="#FFFFFF"
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default AvgLife;